import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, CircleCheck as CheckCircle2, Circle as XCircle, Loader as Loader2, RefreshCw, QrCode, Power, Send, TriangleAlert as AlertTriangle, Smartphone, ShieldCheck, Webhook, Stethoscope } from 'lucide-react';

interface Instance {
  id: string;
  scope: string;
  seller_id: string | null;
  unit_id: string;
  instance_name: string;
  phone_number: string | null;
  status: 'disconnected' | 'connecting' | 'connected' | 'failed';
  qr_code: string | null;
  last_connected_at: string | null;
}

async function callFn(path: string, body: Record<string, unknown>) {
  const { data: { session } } = await supabase.auth.getSession();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Erro ${res.status}`);
  return data;
}

function StatusBox({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className={`rounded-lg border p-3 flex items-start gap-2 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
      {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />}
      <div>
        <div className={`text-sm font-medium ${ok ? 'text-emerald-800' : 'text-amber-800'}`}>{label}</div>
        <div className={`text-xs ${ok ? 'text-emerald-700' : 'text-amber-700'}`}>{ok ? 'OK' : 'Precisa atencao'}</div>
      </div>
    </div>
  );
}

export default function MyWhatsApp() {
  const { user } = useAuth();
  const [instance, setInstance] = useState<Instance | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Olá! Este é um teste do meu WhatsApp conectado ao sistema.');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState<any | null>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState<any | null>(null);
  const [showDiagModal, setShowDiagModal] = useState(false);
  const [aiConfig, setAiConfig] = useState<{ paused_reason: string | null; paused_since: string | null } | null>(null);

  const flash = (type: 'success' | 'error', msg: string) => {
    setBanner({ type, msg });
    setTimeout(() => setBanner(null), 4000);
  };

  useEffect(() => {
    if (user) {
      load();
      loadAgentConfig();
      const t = setInterval(loadAgentConfig, 30000);
      return () => clearInterval(t);
    }
  }, [user]);

  const loadAgentConfig = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('ai_agent_config')
      .select('paused_reason, paused_since')
      .eq('seller_id', user.id)
      .maybeSingle();
    setAiConfig(data ?? null);
  };

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from('whatsapp_instances')
      .select('*')
      .eq('scope', 'seller')
      .eq('seller_id', user.id)
      .maybeSingle();
    setInstance(data);
    setLoading(false);
  };

  const connect = async () => {
    setBusy(true);
    try {
      const res = await callFn('whatsapp-instance/create', { scope: 'seller' });
      setInstance(res.instance);
      if (res.qrcode) {
        setQrCode(res.qrcode);
        setShowQrModal(true);
      } else {
        await openQr();
      }
      flash('success', 'Instância criada. Escaneie o QR Code.');
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro ao criar instância');
    } finally {
      setBusy(false);
    }
  };

  const openQr = async () => {
    setQrLoading(true);
    setShowQrModal(true);
    try {
      const res = await callFn('whatsapp-instance/qrcode', { scope: 'seller' });
      setQrCode(res.qrcode);
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro ao obter QR Code');
      setShowQrModal(false);
    } finally {
      setQrLoading(false);
    }
  };

  const refreshStatus = async () => {
    try {
      await callFn('whatsapp-instance/status', { scope: 'seller' });
      await load();
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro ao consultar status');
    }
  };

  const disconnect = async () => {
    if (!confirm('Desconectar seu WhatsApp?')) return;
    setBusy(true);
    try {
      await callFn('whatsapp-instance/disconnect', { scope: 'seller' });
      await load();
      flash('success', 'WhatsApp desconectado');
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const configureWebhook = async () => {
    setBusy(true);
    try {
      const res = await callFn('whatsapp-instance/configure-webhook', { scope: 'seller' });
      await load();
      if (res?.ok) {
        flash('success', `Robo do agente ligado a este WhatsApp (formato aceito: ${res.variant})`);
      } else {
        flash('error', 'O servidor do WhatsApp aceitou a chamada, mas nao confirmou o registro. Abra "Diagnosticar" para ver os detalhes.');
      }
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro ao configurar');
    } finally {
      setBusy(false);
    }
  };

  const diagnoseWebhook = async () => {
    setDiagLoading(true);
    setDiagResult(null);
    setSimResult(null);
    try {
      const res = await callFn('whatsapp-instance/diagnose-webhook', { scope: 'seller' });
      setDiagResult(res);
      setShowDiagModal(true);
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro no diagnostico');
    } finally {
      setDiagLoading(false);
    }
  };

  const simulateInbound = async () => {
    setSimLoading(true);
    setSimResult(null);
    try {
      const res = await callFn('whatsapp-instance/simulate-inbound', { scope: 'seller' });
      setSimResult(res);
      const refreshed = await callFn('whatsapp-instance/diagnose-webhook', { scope: 'seller' });
      setDiagResult(refreshed);
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro na simulacao');
    } finally {
      setSimLoading(false);
    }
  };

  const runTest = async () => {
    if (!testPhone) return;
    setTestSending(true);
    setTestResult(null);
    try {
      await callFn('whatsapp-instance/test-send', {
        scope: 'seller',
        phone: testPhone,
        message: testMessage,
      });
      setTestResult('Enviado com sucesso!');
    } catch (err) {
      setTestResult(err instanceof Error ? err.message : 'Falha no envio');
    } finally {
      setTestSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const statusBadge = (() => {
    if (!instance) return { label: 'Não configurado', color: 'bg-slate-100 text-slate-700', icon: XCircle };
    if (instance.status === 'connected') return { label: 'Conectado', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 };
    if (instance.status === 'connecting') return { label: 'Aguardando conexão', color: 'bg-amber-100 text-amber-800', icon: Loader2 };
    if (instance.status === 'failed') return { label: 'Falha', color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    return { label: 'Desconectado', color: 'bg-slate-100 text-slate-700', icon: XCircle };
  })();

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-emerald-100 rounded-xl">
          <Smartphone className="w-6 h-6 text-emerald-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Meu WhatsApp</h1>
          <p className="text-sm text-slate-600">Conecte seu WhatsApp pessoal para enviar mensagens direto para seus clientes.</p>
        </div>
      </div>

      {aiConfig?.paused_reason === 'openai_quota_exhausted' && (
        <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-red-400 bg-red-50 shadow-sm">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900 flex-1">
            <div className="font-bold text-base">IA temporariamente indisponivel</div>
            <div className="mt-1">
              A IA nao esta respondendo mensagens no momento porque a conta ficou sem credito.
              Voce pode responder manualmente enquanto isso — a IA volta sozinha assim que o credito for recarregado.
            </div>
          </div>
        </div>
      )}

      {banner && (
        <div className={`p-4 rounded-lg border ${
          banner.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {banner.msg}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Conexão</h2>
            <p className="text-sm text-slate-500">Escaneie o QR Code com o WhatsApp do seu celular.</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusBadge.color}`}>
            <statusBadge.icon className={`w-4 h-4 ${instance?.status === 'connecting' ? 'animate-spin' : ''}`} />
            {statusBadge.label}
          </span>
        </div>

        <div className="p-5 space-y-4">
          {instance?.last_connected_at && (
            <div className="text-sm text-slate-600">
              Última conexão: {new Date(instance.last_connected_at).toLocaleString('pt-BR')}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {!instance ? (
              <button
                onClick={connect}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                Conectar meu WhatsApp
              </button>
            ) : (
              <>
                <button
                  onClick={openQr}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium transition-colors"
                >
                  <QrCode className="w-4 h-4" />
                  {instance.status === 'connected' ? 'Reescanear' : 'QR Code'}
                </button>
                <button
                  onClick={refreshStatus}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-lg font-medium transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Atualizar status
                </button>
                <button
                  onClick={() => setShowTestModal(true)}
                  disabled={instance.status !== 'connected'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-40"
                >
                  <Send className="w-4 h-4" />
                  Testar envio
                </button>
                <button
                  onClick={configureWebhook}
                  disabled={busy || instance.status !== 'connected'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors disabled:opacity-40"
                  title="Reconectar o robo do agente a este WhatsApp"
                >
                  <Webhook className="w-4 h-4" />
                  Ligar agente ao WhatsApp
                </button>
                <button
                  onClick={diagnoseWebhook}
                  disabled={diagLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors disabled:opacity-40"
                  title="Verificar se o servidor do WhatsApp esta enviando as mensagens para o agente"
                >
                  {diagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stethoscope className="w-4 h-4" />}
                  Diagnosticar
                </button>
                <button
                  onClick={disconnect}
                  disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors ml-auto disabled:opacity-40"
                >
                  <Power className="w-4 h-4" />
                  Desconectar
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900 space-y-2">
            <p className="font-medium">Como funciona</p>
            <ul className="list-disc list-inside space-y-1 text-emerald-800">
              <li>Você pode enviar mensagens para seus clientes direto pela Agenda.</li>
              <li>Se seu WhatsApp não estiver conectado, o sistema usa automaticamente o WhatsApp da unidade.</li>
              <li>Seus dados de conexão ficam seguros no sistema — não compartilhamos com terceiros.</li>
            </ul>
          </div>
        </div>
      </div>

      {showQrModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Escaneie no WhatsApp</h3>
              <button onClick={() => setShowQrModal(false)} className="text-slate-500 hover:text-slate-800">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600">
              Abra o WhatsApp no celular &gt; Configurações &gt; Aparelhos conectados &gt; Conectar um aparelho.
            </p>
            <div className="flex items-center justify-center min-h-[280px]">
              {qrLoading ? (
                <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
              ) : qrCode ? (
                <img
                  src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                  alt="QR Code"
                  className="w-64 h-64"
                />
              ) : (
                <p className="text-sm text-slate-500">QR Code indisponível</p>
              )}
            </div>
            <button
              onClick={openQr}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Atualizar QR
            </button>
            <button
              onClick={() => { setShowQrModal(false); refreshStatus(); }}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" />
              Já escaneei
            </button>
          </div>
        </div>
      )}

      {showTestModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Testar envio</h3>
              <button onClick={() => { setShowTestModal(false); setTestResult(null); }} className="text-slate-500 hover:text-slate-800">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Telefone (com DDD)</label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="11987654321"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mensagem</label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
              />
            </div>
            {testResult && (
              <div className={`p-3 rounded-lg text-sm ${testResult === 'Enviado com sucesso!' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {testResult}
              </div>
            )}
            <button
              onClick={runTest}
              disabled={testSending || !testPhone}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-60"
            >
              {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar teste
            </button>
          </div>
        </div>
      )}

      {showDiagModal && diagResult && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 bg-black/40" onClick={() => setShowDiagModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-slate-700" />
                <h3 className="text-lg font-semibold text-slate-900">Diagnostico do agente</h3>
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200" title="Se voce nao ve esta etiqueta, o navegador ainda esta com a versao antiga. Feche a aba e abra de novo.">
                  <ShieldCheck className="w-3 h-3" />
                  v3 - auto-pausa
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={diagnoseWebhook} disabled={diagLoading} className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
                  <RefreshCw className={`w-3.5 h-3.5 ${diagLoading ? 'animate-spin' : ''}`} />
                  Atualizar
                </button>
                <button onClick={() => setShowDiagModal(false)} className="text-slate-500 hover:text-slate-900 px-2">Fechar</button>
              </div>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="rounded-lg p-3 border border-slate-200 bg-slate-50 text-slate-700 text-xs flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Como testar agora:</strong> mande "oi" pelo seu telefone pessoal para o numero do WhatsApp da vendedora, aguarde 5 segundos e clique em <strong>Atualizar</strong> aqui em cima. A linha nova em <strong>Chamadas recebidas</strong> deve trazer um <em>Desfecho</em> — se aparecer verde (<code className="font-mono">handled_brain_invoked</code>), a IA foi acionada e o bloco <strong>O que o cerebro da IA fez</strong> mostra o que ela respondeu ou por que ficou muda.
                </div>
              </div>
              {(() => {
                const brainLog = (diagResult.recent_brain_log as any[]) || [];
                const last5 = brainLog.slice(0, 5);
                const quotaHits = last5.filter(b => b.outcome === 'openai_quota_exhausted' || (b.outcome === 'openai_error' && (b.outcome_detail || '').includes('insufficient_quota'))).length;
                const replies = last5.filter(b => b.outcome === 'replied').length;
                const errors = last5.filter(b => b.outcome === 'openai_error').length;
                if (quotaHits >= 2) {
                  return (
                    <div className="rounded-lg p-4 border bg-red-50 border-red-300 text-red-900">
                      <strong>IA pausada por falta de credito na OpenAI.</strong> {quotaHits} das ultimas {last5.length} tentativas retornaram cota esgotada. Recarregue a conta em <a href="https://platform.openai.com/settings/organization/billing" target="_blank" rel="noopener noreferrer" className="underline font-semibold">platform.openai.com/settings/organization/billing</a> e a IA volta sozinha na proxima mensagem que chegar.
                    </div>
                  );
                }
                if (replies >= 2 && errors === 0) {
                  return (
                    <div className="rounded-lg p-4 border bg-emerald-50 border-emerald-200 text-emerald-800">
                      <strong>Tudo certo</strong> — a IA respondeu {replies} das ultimas {last5.length} mensagens normalmente.
                    </div>
                  );
                }
                if (errors >= 2) {
                  return (
                    <div className="rounded-lg p-4 border bg-amber-50 border-amber-200 text-amber-900">
                      <strong>OpenAI retornou erro nas ultimas {errors} tentativas.</strong> Veja o detalhe no bloco "O que o cerebro da IA fez" abaixo.
                    </div>
                  );
                }
                return (
                  <div className={`rounded-lg p-4 border ${diagResult.overall_ok && (diagResult.recent_inbound_for_instance?.length ?? 0) > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : diagResult.overall_ok ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                    {diagResult.overall_ok && (diagResult.recent_inbound_for_instance?.length ?? 0) > 0 && (
                      <>WhatsApp registrado E {diagResult.recent_inbound_for_instance.length} chamada(s) recebida(s) nos ultimos 10 minutos. Mande "oi" novo e clique em Atualizar para ver o desfecho.</>
                    )}
                    {diagResult.overall_ok && (diagResult.recent_inbound_for_instance?.length ?? 0) === 0 && (
                      <>URL registrada corretamente no WhatsApp, mas <strong>nenhum evento chegou ainda no nosso servidor</strong> nos ultimos 10 minutos. Clique em "Simular chamada" abaixo para descobrir se o problema esta no nosso servidor ou no servidor do WhatsApp.</>
                    )}
                    {!diagResult.overall_ok && <>Encontramos onde parou. Veja abaixo o que falta.</>}
                  </div>
                );
              })()}

              <div className="grid grid-cols-2 gap-3">
                <StatusBox label="Endereco salvo aqui" ok={diagResult.saved_ok} />
                <StatusBox label="Aceito pelo WhatsApp" ok={diagResult.evolution_ok} />
              </div>

              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-2">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">O que o WhatsApp respondeu</div>
                <div><span className="text-slate-500">URL registrada:</span> <span className="font-mono text-xs break-all">{diagResult.evolution?.url || 'nenhuma'}</span></div>
                <div><span className="text-slate-500">URL esperada:</span> <span className="font-mono text-xs break-all">{diagResult.expected_url}</span></div>
                <div><span className="text-slate-500">Ativo:</span> {diagResult.evolution?.enabled === true ? 'sim' : diagResult.evolution?.enabled === false ? 'nao' : 'nao informado'}</div>
                <div><span className="text-slate-500">Eventos:</span> <span className="font-mono text-xs">{Array.isArray(diagResult.evolution?.events) ? diagResult.evolution.events.join(', ') : (diagResult.evolution?.events ? JSON.stringify(diagResult.evolution.events) : 'nenhum')}</span></div>
              </div>

              {diagResult.last_error_body && diagResult.evolution?.url_matches_expected !== true && (
                <div className="border border-rose-200 bg-rose-50 rounded-lg p-4 space-y-1">
                  <div className="text-xs uppercase tracking-wide text-rose-700 font-semibold">Resposta de erro do servidor WhatsApp (HTTP {diagResult.last_error_status} — formato {diagResult.last_error_variant})</div>
                  <pre className="text-xs font-mono text-rose-900 whitespace-pre-wrap break-all max-h-40 overflow-auto">{diagResult.last_error_body}</pre>
                </div>
              )}

              <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-blue-900">Testar se o nosso servidor esta recebendo</div>
                    <div className="text-xs text-blue-800 mt-1">Envia uma chamada de teste direto no nosso endereco. Se aparecer no log logo depois, o nosso lado esta OK e o problema e o servidor do WhatsApp nao conseguir alcancar aqui.</div>
                  </div>
                  <button
                    onClick={simulateInbound}
                    disabled={simLoading}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm rounded-lg whitespace-nowrap"
                  >{simLoading ? 'Testando...' : 'Simular chamada'}</button>
                </div>
                {simResult && (
                  <div className="space-y-2">
                    <div className={`text-sm font-semibold ${simResult.gateway_ok ? 'text-emerald-800' : 'text-rose-800'}`}>
                      {simResult.gateway_ok
                        ? 'Nosso servidor recebeu e registrou a chamada de teste. O problema NAO e o nosso lado — e o servidor do WhatsApp que nao esta enviando ou nao alcanca aqui.'
                        : 'Nossa chamada de teste NAO chegou ao log. O gateway do Supabase ou a funcao esta rejeitando. Precisamos investigar do nosso lado.'}
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-blue-100 text-blue-900">
                        <tr>
                          <th className="text-left px-2 py-1">Variante</th>
                          <th className="text-left px-2 py-1">Http</th>
                          <th className="text-left px-2 py-1">Resposta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(simResult.attempts as any[]).map((a: any, i: number) => (
                          <tr key={i} className="border-t border-blue-100">
                            <td className="px-2 py-1 font-mono">{a.variant}</td>
                            <td className="px-2 py-1">{a.status ?? 'erro'}</td>
                            <td className="px-2 py-1 font-mono text-blue-900 truncate max-w-xs">{a.body || a.error || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center justify-between">
                  <span>Chamadas recebidas nos ultimos 10 min</span>
                  <span className="font-semibold text-slate-700 normal-case tracking-normal">{diagResult.recent_inbound_for_instance?.length ?? 0} para esta instancia / {diagResult.recent_inbound_all?.length ?? 0} no total</span>
                </div>
                {(diagResult.recent_inbound_all?.length ?? 0) === 0 ? (
                  <div className="p-4 text-sm text-slate-500">Nenhuma chamada chegou. Clique em "Simular chamada" acima para descobrir se o problema esta no nosso servidor ou no servidor do WhatsApp.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2">Quando</th>
                        <th className="text-left px-3 py-2">Evento</th>
                        <th className="text-left px-3 py-2">Instancia</th>
                        <th className="text-left px-3 py-2">Desfecho</th>
                        <th className="text-left px-3 py-2">Detalhe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagResult.recent_inbound_all as any[]).slice(0, 15).map((r: any, i: number) => {
                        const hasOutcome = !!r.outcome;
                        const outcome = r.outcome || 'evento_antes_da_correcao';
                        const isReplied = outcome === 'handled_brain_invoked';
                        const isIgnored = outcome.startsWith('ignored_') || outcome === 'event_missing' || outcome.startsWith('event_ignored_');
                        const isError = outcome === 'error';
                        const outcomeColor = !hasOutcome
                          ? 'text-slate-500 bg-slate-100 border border-dashed border-slate-300'
                          : isReplied
                            ? 'text-emerald-700 bg-emerald-50'
                            : isError
                              ? 'text-rose-700 bg-rose-50'
                              : isIgnored
                                ? 'text-amber-700 bg-amber-50'
                                : 'text-slate-600 bg-slate-50';
                        return (
                          <tr key={i} className="border-t border-slate-100 align-top">
                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.arrived_at ? new Date(r.arrived_at).toLocaleTimeString('pt-BR') : '-'}</td>
                            <td className="px-3 py-2 font-mono">{r.event || '-'}</td>
                            <td className="px-3 py-2 font-mono text-slate-500 truncate max-w-[140px]">{r.instance_name || '-'}</td>
                            <td className="px-3 py-2"><span className={`inline-block font-mono px-2 py-0.5 rounded ${outcomeColor}`}>{outcome}</span></td>
                            <td className="px-3 py-2 text-slate-600 font-mono text-[11px] break-all">{hasOutcome ? (r.outcome_detail || '') : 'evento anterior ao deploy da correcao — mande "oi" de novo para testar'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {Array.isArray(diagResult.recent_brain_log) && diagResult.recent_brain_log.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center justify-between">
                    <span>O que o cerebro da IA fez</span>
                    <span className="font-semibold text-slate-700 normal-case tracking-normal">{diagResult.recent_brain_log.length} chamada(s)</span>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2">Quando</th>
                        <th className="text-left px-3 py-2">Telefone</th>
                        <th className="text-left px-3 py-2">Recebeu</th>
                        <th className="text-left px-3 py-2">Desfecho</th>
                        <th className="text-left px-3 py-2">Detalhe / Resposta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagResult.recent_brain_log as any[]).slice(0, 12).map((b: any, i: number) => {
                        const isReplied = b.outcome === 'replied';
                        const isError = b.outcome === 'openai_error' || b.outcome === 'send_error' || b.outcome === 'no_openai_key' || b.outcome === 'fatal_error';
                        const outcomeColor = isReplied
                          ? 'text-emerald-700 bg-emerald-50'
                          : isError
                            ? 'text-rose-700 bg-rose-50'
                            : 'text-amber-700 bg-amber-50';
                        return (
                          <tr key={i} className="border-t border-slate-100 align-top">
                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{b.invoked_at ? new Date(b.invoked_at).toLocaleTimeString('pt-BR') : '-'}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{b.phone || '-'}</td>
                            <td className="px-3 py-2 text-slate-700 max-w-[220px] truncate" title={b.incoming_text || ''}>{b.incoming_text || ''}</td>
                            <td className="px-3 py-2"><span className={`inline-block font-mono px-2 py-0.5 rounded ${outcomeColor}`}>{b.outcome}</span></td>
                            <td className="px-3 py-2 text-slate-600 text-[11px] break-all max-w-[280px]">
                              {b.reply_text
                                ? <span className="text-emerald-800">{b.reply_text}</span>
                                : <span className="font-mono">{b.outcome_detail || ''}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {Array.isArray(diagResult.recent_attempts) && diagResult.recent_attempts.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 font-semibold">Ultimas tentativas de registro</div>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2">Quando</th>
                        <th className="text-left px-3 py-2">Formato</th>
                        <th className="text-left px-3 py-2">Http</th>
                        <th className="text-left px-3 py-2">Confirmado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagResult.recent_attempts.slice(0, 8).map((a: any, i: number) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-600">{a.attempted_at ? new Date(a.attempted_at).toLocaleString('pt-BR') : '-'}</td>
                          <td className="px-3 py-2 font-mono">{a.payload_variant}</td>
                          <td className="px-3 py-2">{a.response_status ?? '-'}</td>
                          <td className="px-3 py-2">{a.verified ? <span className="text-emerald-700">sim</span> : <span className="text-slate-500">nao</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!diagResult.overall_ok && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 text-blue-900 text-sm">
                  <div className="font-semibold mb-1">O que fazer</div>
                  {!diagResult.saved_ok && <div>1. Clique em "Ligar agente ao WhatsApp" para gravar o endereco.</div>}
                  {diagResult.saved_ok && !diagResult.evolution_ok && (
                    <div>1. Clique de novo em "Ligar agente ao WhatsApp". Se ainda assim nao passar, provavelmente o servidor do WhatsApp esta com a instancia offline ou bloqueando os eventos. Reconecte a instancia (desconectar/QR de novo).</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
